import React from 'react';
import './BuildPlateWarningPopup.css';
import { useTranslation } from 'react-i18next';

interface BuildPlateWarningPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue?: () => void;
  printerName: string;
  buildPlateSize: string;
  incompatibleModels: string[];
  canContinue: boolean; // If false, show error; if true, show warning with continue option
}

export function BuildPlateWarningPopup({ 
  isOpen, 
  onClose, 
  onContinue, 
  printerName,
  buildPlateSize,
  incompatibleModels,
  canContinue
}: BuildPlateWarningPopupProps) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="build-plate-warning-overlay" onClick={onClose}>
      <div className="build-plate-warning-popup" onClick={(e) => e.stopPropagation()}>
        <div className="build-plate-warning-header">
          <div className="build-plate-warning-header-content">
            <h2>Build Plate Size Warning</h2>
          </div>
        </div>
        
        <div className="build-plate-warning-content">
          <div className="warning-message">
            <p>
              The following model(s) are too large for the <strong>{printerName}</strong> build plate 
              ({buildPlateSize}mm), even when considering optimal rotation:
            </p>
            
            <div className="incompatible-models">
              {incompatibleModels.map((modelName, index) => (
                <div key={index} className="incompatible-model">
                  <span className="bullet">•</span>
                  <span className="model-name">{modelName}</span>
                </div>
              ))}
            </div>
            
            <h4>Suggested solutions:</h4>
            <ol>
              <li>
                <img src="/checkmark_icon.svg" alt="" className="solution-icon" />
                <span>Scale down the models to fit</span>
              </li>
              <li>
                <img src="/checkmark_icon.svg" alt="" className="solution-icon" />
                <span>Split large models into smaller parts</span>
              </li>
              <li>
                <img src="/checkmark_icon.svg" alt="" className="solution-icon" />
                <span>Choose a printer with a larger build plate</span>
              </li>
              {canContinue && (
                <li>
                  <img src="/checkmark_icon.svg" alt="" className="solution-icon" />
                  <span>Continue without these models (only remaining models will be processed)</span>
                </li>
              )}
            </ol>

            {!canContinue && (
              <div className="error-notice">
                Please scale down your models or choose a printer with a larger build plate.
              </div>
            )}
          </div>
          
          <div className="popup-buttons">
            <button className="cancel-button" onClick={onClose}>
              {canContinue ? 'Cancel' : 'UNDERSTOOD'}
            </button>
            {canContinue && onContinue && (
              <button className="continue-button" onClick={onContinue}>
                Continue with Compatible Models
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 